import { useEffect, useEffectEvent, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { autorun } from 'mobx'
import debounce from 'lodash/debounce'
import { getBookPhotosUids } from 'services/PhotoBook/photoBookUtils'
import { type StoredPhotoBook } from 'services/PhotoBook/photoBookSaver.types'
import { deepToJS } from 'utils/mobx/deepToJS'
import { useKeys } from 'services/KeysProvider'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import { useCartStore } from 'stores/cartStore/CartStoreProvider'
import {
  usePhotoBankStore,
  usePhotoBooksStore,
  useTilesStore,
} from 'stores/RootStoreProvider'
import { FLOWS } from '../userCart.consts'
import { hasStoredCartId, saveCart } from '../userCartService'
import { buildAbandonedCart, buildBrazeAttributes } from './buildAbandonedCart'
import { useAbandonedCartRestore } from './useAbandonedCartRestore'
import {
  type AbandonedCartSnapshot,
  type AbandonedCartTileSnapshot,
} from './abandonedCart.types'

const AUTOSAVE_WATCH_DELAY_MS = 150
const AUTOSAVE_DEBOUNCE_MS = 3000

function collectBagPhotoUids(
  tileSnapshots: AbandonedCartTileSnapshot[],
  books: StoredPhotoBook[]
): Set<string> {
  const uids = new Set(tileSnapshots.flatMap((tile) => tile.photoUids))
  books.forEach((book) => {
    getBookPhotosUids(book.cover, book.pages).forEach((uid) => {
      if (uid) uids.add(uid)
    })
  })
  return uids
}

function AbandonedCartAutosave() {
  const cartStore = useCartStore()
  const tilesStore = useTilesStore()
  const photoBooksStore = usePhotoBooksStore()
  const photoBankStore = usePhotoBankStore()
  const { t } = useTranslation()
  const { ipCountry } = useKeys()
  const experimentManager = useExperimentManager()
  const isPriceTierListEnabled = experimentManager.isEnabled(
    'pricing_photo_book_price_tier'
  )

  const buildAbandonedCartItems = useEffectEvent(
    (snapshot: AbandonedCartSnapshot) =>
      buildAbandonedCart(snapshot, t, ipCountry, isPriceTierListEnabled)
  )

  // These attributes live on the Braze profile, not the device, so an empty write
  // from a second browser would erase a real bag built elsewhere. Emptiness is
  // only written once this session has written a bag of its own, and only while a
  // stored cart id survives — checkout clears that id, and writing afterwards would
  // mint a fresh one and resurrect the cart the order just closed. Writing emptiness
  // flips the flag back, so a bag that stays empty is written once. The count is of
  // items, not cart rows: a book row whose book has not loaded yields no item.
  const hasWrittenBagRef = useRef(false)

  useEffect(() => {
    const save = debounce((snapshot: AbandonedCartSnapshot) => {
      const { items, previewItems } = buildAbandonedCartItems(snapshot)
      if (
        items.length === 0 &&
        (!hasWrittenBagRef.current || !hasStoredCartId(FLOWS.CART))
      )
        return
      hasWrittenBagRef.current = items.length > 0
      saveCart({
        flow: FLOWS.CART,
        cartItems: items,
        emailTemplateParams: buildBrazeAttributes(items),
        previewItems,
      })
    }, AUTOSAVE_DEBOUNCE_MS)

    const stopWatchingBag = autorun(
      () => {
        const books = deepToJS(photoBooksStore.books)
        const isAnyBookWithheld = books.some(
          (book) => !photoBooksStore.remoteSyncer.isBookPushable(book)
        )
        if (isAnyBookWithheld) {
          save.cancel()
          return
        }
        const cartItems = deepToJS(cartStore.cartItems)
        const tileSnapshots = tilesStore.tiles.map((tile) => ({
          tileData: tile.toTileData(),
          photoUids: tile.getPhotoUids(),
        }))
        const bagPhotoUids = collectBagPhotoUids(tileSnapshots, books)
        save({
          cartItems,
          tiles: tileSnapshots,
          books,
          remotePhotos: deepToJS(
            photoBankStore.remotePhotos.filter((photo) =>
              bagPhotoUids.has(photo.uid)
            )
          ),
        })
      },
      { name: 'AbandonedCartAutosave', delay: AUTOSAVE_WATCH_DELAY_MS }
    )

    return () => {
      save.flush()
      stopWatchingBag()
    }
  }, [cartStore, tilesStore, photoBooksStore, photoBankStore])

  return null
}

export function AbandonedCartSync() {
  const isRestoreComplete = useAbandonedCartRestore()

  return isRestoreComplete ? <AbandonedCartAutosave /> : null
}
